import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hydrateRekordboxSyncState } from '../redux/slices/rekordboxSlice';
import { hydrateSeratoSyncState } from '../redux/slices/seratoSlice';
import { hydrateTraktorSyncState } from '../redux/slices/traktorSlice';
import { hydrateVirtualDJSyncState } from '../redux/slices/virtualdjSlice';

const HYDRATE = {
    rekordbox: hydrateRekordboxSyncState,
    serato: hydrateSeratoSyncState,
    traktor: hydrateTraktorSyncState,
    virtualdj: hydrateVirtualDJSyncState,
};

// The pre-017 renderer stamp. Never read again — a value of "null" stuck as a
// truthy string and read as "Never" beside a list of imported crates.
const LEGACY_KEYS = Object.keys(HYDRATE).map((app) => `${app}.lastSyncTime`);

const apply = (dispatch, state) => {
    for (const [app, hydrate] of Object.entries(HYDRATE)) dispatch(hydrate(state[app] || null));
};

/**
 * "Last synced" per DJ app belongs to the ACCOUNT and lives in the main
 * process (dj_sync_state, stamped by the import handlers). Two feeds:
 *  - a fetch on boot and whenever the signed-in user changes — the
 *    account-switch wipe resets the four DJ slices, this refills them;
 *  - the main process's push (djlibrary:sync-state) on every owner change
 *    and after each import. The push is not optional: auth.user is set from
 *    a cached profile before auth:me reaches the main process, so the boot
 *    fetch can run before the owner is known and see the signed-out bucket.
 * Always mounted (App.js), renders nothing.
 */
export default function DjSyncStateHydrator() {
    const dispatch = useDispatch();
    const userId = useSelector((s) => s.auth?.user?.id) || null;

    useEffect(() => {
        for (const key of LEGACY_KEYS) { try { localStorage.removeItem(key); } catch { /* private mode */ } }
    }, []);

    useEffect(() => {
        const api = window.electron?.djlibrary;
        if (!api?.onSyncState) return undefined;
        return api.onSyncState((state) => { if (state) apply(dispatch, state); });
    }, [dispatch]);

    useEffect(() => {
        const api = window.electron?.djlibrary;
        if (!api?.getSyncState) return undefined;
        let live = true;
        api.getSyncState().then((state) => {
            if (live && state) apply(dispatch, state);
        }).catch(() => { /* sidebar keeps "Never" until the next sync stamps it */ });
        return () => { live = false; };
    }, [dispatch, userId]);

    return null;
}
